/**
 * Buyer auth context — small hook that mirrors localStorage tokens into React
 * state and exposes `signOut()`. Token attach/refresh is handled by the axios
 * interceptor in lib/api.js, so this file is intentionally tiny.
 */
import React, { createContext, useContext, useEffect, useState } from "react";

import {
  clearAuth,
  getStoredBuyer,
  getStoredAccessToken,
  getStoredRefreshToken,
  logoutAuth,
} from "./api";

const BuyerAuthCtx = createContext({
  buyer: null,
  isAuthenticated: false,
  signOut: () => {},
});

export function BuyerAuthProvider({ children }) {
  const [buyer, setBuyer] = useState(() => getStoredBuyer());
  const [hasToken, setHasToken] = useState(() => Boolean(getStoredAccessToken()));

  useEffect(() => {
    const onChange = () => {
      setBuyer(getStoredBuyer());
      setHasToken(Boolean(getStoredAccessToken()));
    };
    window.addEventListener("shutterkart-auth-change", onChange);
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener("shutterkart-auth-change", onChange);
      window.removeEventListener("storage", onChange);
    };
  }, []);

  const signOut = async () => {
    const refresh = getStoredRefreshToken();
    try {
      if (refresh) await logoutAuth(refresh);
    } catch {
      /* swallow */
    }
    clearAuth();
  };

  return (
    <BuyerAuthCtx.Provider
      value={{ buyer, isAuthenticated: hasToken && Boolean(buyer), signOut }}
    >
      {children}
    </BuyerAuthCtx.Provider>
  );
}

export function useBuyerAuth() {
  return useContext(BuyerAuthCtx);
}
